import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  RestoreFromTrash as RestoreIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Receipt as ReceiptIcon,
  RequestQuote as RequestQuoteIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { Facture, FactureStatus } from '../../types/facture';
import { User } from '../../types/auth';
import { Client } from '../../types/client';
import { facturesAPI, userAPI, auditLogsAPI, clientsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import { getModuleTableTheme } from '../../theme/tableTheme';
import CustomTablePagination from '../../components/CustomTablePagination';
import PermissionButton from '../../components/PermissionButton';
import { extractErrorMessage } from '../../utils/errorHandler';
import usePdfPreview from '../../hooks/usePdfPreview';
import FacturePreview from './FacturePreview';
import FactureFormDialog from './FactureFormDialog';
// generateFacturePDF / generateFactureAcomptePDF are dynamically imported
// in the print handler so the @react-pdf/renderer chunk only loads when
// the user clicks Print.
import ColumnFilter from '../../components/ColumnFilter';
import { useColumnFilters } from '../../hooks/useColumnFilters';

const COMMERCIAL_COLOR = '#4CAF50';

const FACTURE_FILTER_SCHEMA = {
  date:         { type: 'dateRange',    paramFrom: 'date_from', paramTo: 'date_to' },
  client_id:    { type: 'autocomplete', param: 'client_id', transform: (v: string) => Number(v) },
  reference:    { type: 'text',         param: 'reference' },
  type:         { type: 'select',       param: 'type' },
  status:       { type: 'select',       param: 'status' },
  bc_numero:    { type: 'text',         param: 'bc_numero' },
  bl_reference: { type: 'text',         param: 'bl_reference' },
  designation:  { type: 'text',         param: 'designation' },
  montant:      { type: 'numberRange',  paramMin: 'montant_min', paramMax: 'montant_max' },
  user_id:      { type: 'autocomplete', param: 'user_id', transform: (v: string) => Number(v) },
} as const;

const FactureList: React.FC = () => {
  const [facturesList, setFacturesList] = useState<Facture[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deletedFacturesList, setDeletedFacturesList] = useState<Facture[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [previewFacture, setPreviewFacture] = useState<Facture | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [editFactureId, setEditFactureId] = useState<number | undefined>(undefined);
  const [factureType, setFactureType] = useState<'normale' | 'acompte'>('normale');
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  // Open the facture PDF in the preview viewer (acompte vs normale generator).
  const previewFacturePdf = (facture: Facture) => {
    openPreview({
      getBlob: async () => {
        if (facture.type === 'acompte') {
          const { getFactureAcomptePDFBlob } = await import('../../utils/factureAcomptePdfReactPdf');
          return getFactureAcomptePDFBlob(facture);
        }
        const { getFacturePDFBlob } = await import('../../utils/facturePdfReactPdf');
        return getFacturePDFBlob(facture);
      },
      fileName: `${facture.reference}.pdf`,
    });
    auditLogsAPI.logPdfDownload(
      facture.type === 'acompte' ? 'FactureAcompte' : 'Facture',
      facture.id,
      `Aperçu PDF - ${facture.reference}`,
    );
  };

  // Column filters
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    FACTURE_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );
  const [users, setUsers] = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [summary, setSummary] = useState({ chiffre_affaires: 0, total_encaisse: 0, total_en_cours: 0 });
  const { user } = useAuth();
  // Same gating pattern as CommercialOverview's "Montant Total des Devis"
  const canSeeAmounts = !!user?.role && ['Manager', 'Developer', 'admin'].includes(user.role);

  // Fetch users and clients for filter dropdowns
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await userAPI.getUsers(0, 1000);
        setUsers(data);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    const fetchClients = async () => {
      try {
        const data = await clientsAPI.getAllForDropdown();
        setClients(data);
      } catch (err) {
        console.error('Error fetching clients:', err);
      }
    };
    fetchUsers();
    fetchClients();
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleClearAllFilters = () => {
    reset();
    setSearchTerm('');
  };

  const fetchFactures = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      // status and user_id go positionally; everything else into the filters bag
      const { status, user_id, ...filterBag } = toQueryParams();

      const response = await facturesAPI.getPaginated(
        skip,
        rowsPerPage,
        debouncedSearch || undefined,
        (status as string) || undefined,
        (user_id as number | undefined),
        filterBag
      );
      setFacturesList(response.items);
      setTotalCount(response.total);
      if (response.summary) {
        setSummary(response.summary);
      }
    } catch (err) {
      showError('Erreur lors du chargement des factures');
      console.error('Error fetching factures:', err);
      setFacturesList([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filters]);

  const fetchDeletedFactures = async () => {
    try {
      setLoading(true);
      const data = await facturesAPI.getDeleted();
      setDeletedFacturesList(data);
    } catch (err) {
      showError('Erreur lors du chargement des factures supprimées');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 0) {
      fetchFactures();
    }
  }, [fetchFactures, tabValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    if (newValue === 1) {
      fetchDeletedFactures();
    }
  };

  const handleDelete = async (facture: Facture) => {
    try {
      await facturesAPI.delete(facture.id);
      showSuccess('Facture supprimée avec succès');
      fetchFactures();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handleRestore = async (facture: Facture) => {
    showConfirm(
      `Êtes-vous sûr de vouloir restaurer la facture "${facture.reference}" ?`,
      async () => {
        try {
          await facturesAPI.restore(facture.id);
          showSuccess('Facture restaurée avec succès');
          fetchDeletedFactures();
          fetchFactures();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      {
        title: 'Confirmer la restauration',
        confirmText: 'Restaurer',
        cancelText: 'Annuler',
      }
    );
  };

  const handleOpenAddMenu = (event: React.MouseEvent<Element>) => {
    setAddMenuAnchor(event.currentTarget as HTMLElement);
  };

  const handleCloseAddMenu = () => {
    setAddMenuAnchor(null);
  };

  const handleOpenNewFacture = (type: 'normale' | 'acompte') => {
    setEditFactureId(undefined);
    setFactureType(type);
    setFormMinimized(false);
    setFormDialogOpen(true);
    handleCloseAddMenu();
  };

  const handleOpenEditFacture = (factureId: number, type?: 'normale' | 'acompte') => {
    setEditFactureId(factureId);
    setFactureType(type || 'normale');
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setFormMinimized(false);
    setEditFactureId(undefined);
    setFactureType('normale');
  };

  const handleFormSuccess = () => {
    fetchFactures();
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case FactureStatus.EN_COURS:
        return <Chip label="En Cours" color="warning" size="small" />;
      case FactureStatus.ENCAISSE:
        return <Chip label="Encaissé" color="success" size="small" />;
      case FactureStatus.IMPAYE:
        return <Chip label="Impayé" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get BL references as comma-separated string (for display)
  const getBLReferences = (facture: Facture): string => {
    if (!facture.bons_livraison || facture.bons_livraison.length === 0) {
      return '-';
    }
    return facture.bons_livraison.map(bl => bl.reference).join(', ');
  };

  // Get BL references as array for tooltip (one per line)
  const getBLReferencesForTooltip = (facture: Facture): React.ReactNode => {
    if (!facture.bons_livraison || facture.bons_livraison.length === 0) {
      return '-';
    }
    return (
      <Box>
        {facture.bons_livraison.map((bl, index) => (
          <Box key={index}>{bl.reference}</Box>
        ))}
      </Box>
    );
  };

  // Get BC numbers for tooltip (one per line)
  const getBCNumerosForTooltip = (facture: Facture): React.ReactNode => {
    if (!facture.bc_numero) {
      return '-';
    }
    const bcNumbers = facture.bc_numero.split(', ');
    if (bcNumbers.length === 1) {
      return facture.bc_numero;
    }
    return (
      <Box>
        {bcNumbers.map((bc, index) => (
          <Box key={index}>{bc}</Box>
        ))}
      </Box>
    );
  };

  // Get designations as comma-separated string (for display)
  const getDesignations = (facture: Facture): string => {
    if (!facture.items || facture.items.length === 0) {
      return '-';
    }
    return facture.items.map(item => item.designation).join(', ');
  };

  // Get designations for tooltip (one per line)
  const getDesignationsForTooltip = (facture: Facture): React.ReactNode => {
    if (!facture.items || facture.items.length === 0) {
      return '-';
    }
    return (
      <Box>
        {facture.items.map((item, index) => (
          <Box key={index}>• {item.designation}</Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', py: 3, px: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Gestion des Factures</Typography>
        {tabValue === 0 && (
          <>
            <PermissionButton
              moduleName="Commercial"
              operation="add"
              miniModuleName="Factures"
              variant="contained"
              onClick={handleOpenAddMenu}
              startIcon={<AddIcon />}
              endIcon={<ArrowDropDownIcon />}
              sx={{
                backgroundColor: '#4CAF50',
                '&:hover': { backgroundColor: '#388E3C' },
              }}
            >
              Nouvelle Facture
            </PermissionButton>
            <Menu
              anchorEl={addMenuAnchor}
              open={Boolean(addMenuAnchor)}
              onClose={handleCloseAddMenu}
            >
              <MenuItem onClick={() => handleOpenNewFacture('normale')}>
                <ListItemIcon><ReceiptIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Facture Normale</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleOpenNewFacture('acompte')}>
                <ListItemIcon><RequestQuoteIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Facture d'Acompte</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`Factures Actives (${totalCount})`} />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <>
          {/* Search + KPI Cards Row */}
          <Box display="flex" gap={2} mb={2} alignItems="center">
            <TextField
              placeholder="Rechercher par référence ou client..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {hasActive() && (
              <Button
                size="small"
                startIcon={<FilterListOffIcon />}
                onClick={handleClearAllFilters}
                sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                Effacer les filtres
              </Button>
            )}
            {canSeeAmounts && (
              <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
                <Card sx={{ bgcolor: '#E8EAF6', borderLeft: '3px solid #3F51B5', minWidth: 160 }}>
                  <CardContent sx={{ py: 0.5, px: 1.5, '&:last-child': { pb: 0.5 } }}>
                    <Typography variant="caption" color="text.secondary">CHIFFRE D'AFFAIRES</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#303F9F' }}>
                      {Number(summary.chiffre_affaires).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ bgcolor: '#FFF3E0', borderLeft: '3px solid #FF9800', minWidth: 160 }}>
                  <CardContent sx={{ py: 0.5, px: 1.5, '&:last-child': { pb: 0.5 } }}>
                    <Typography variant="caption" color="text.secondary">EN COURS</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#E65100' }}>
                      {Number(summary.total_en_cours).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ bgcolor: '#E8F5E9', borderLeft: '3px solid #4CAF50', minWidth: 160 }}>
                  <CardContent sx={{ py: 0.5, px: 1.5, '&:last-child': { pb: 0.5 } }}>
                    <Typography variant="caption" color="text.secondary">ENCAISSÉ</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                      {Number(summary.total_encaisse).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>

          <TableContainer component={Paper} sx={getModuleTableTheme('commercial')}>
            <Table size="small" sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Date
                      <ColumnFilter
                        type="dateRange"
                        value={filters.date}
                        onChange={(v) => update('date', v)}
                        label="date"
                        color={COMMERCIAL_COLOR}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Client
                      <ColumnFilter
                        type="autocomplete"
                        value={filters.client_id}
                        onChange={(v) => update('client_id', v)}
                        label="Client"
                        color={COMMERCIAL_COLOR}
                        options={clients.map(c => ({ value: c.id.toString(), label: c.name }))}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Facture N°
                      <ColumnFilter
                        type="text"
                        value={filters.reference}
                        onChange={(v) => update('reference', v)}
                        label="référence"
                        color={COMMERCIAL_COLOR}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Type
                      <ColumnFilter
                        type="select"
                        value={filters.type}
                        onChange={(v) => update('type', v)}
                        label="type"
                        color={COMMERCIAL_COLOR}
                        options={[
                          { value: 'normale', label: 'Normale' },
                          { value: 'acompte', label: 'Acompte' },
                        ]}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      BC N°
                      <ColumnFilter
                        type="text"
                        value={filters.bc_numero}
                        onChange={(v) => update('bc_numero', v)}
                        label="BC N°"
                        color={COMMERCIAL_COLOR}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      BL / Devis N°
                      <ColumnFilter
                        type="text"
                        value={filters.bl_reference}
                        onChange={(v) => update('bl_reference', v)}
                        label="BL / Devis N°"
                        color={COMMERCIAL_COLOR}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Libellés
                      <ColumnFilter
                        type="text"
                        value={filters.designation}
                        onChange={(v) => update('designation', v)}
                        label="libellé"
                        color={COMMERCIAL_COLOR}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      Montant
                      <ColumnFilter
                        type="numberRange"
                        value={filters.montant}
                        onChange={(v) => update('montant', v)}
                        label="montant"
                        color={COMMERCIAL_COLOR}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      Statut
                      <ColumnFilter
                        type="select"
                        value={filters.status}
                        onChange={(v) => update('status', v)}
                        label="statut"
                        color={COMMERCIAL_COLOR}
                        options={[
                          { value: 'en_cours', label: 'En Cours' },
                          { value: 'encaisse', label: 'Encaissé' },
                          { value: 'impaye', label: 'Impayé' },
                        ]}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      Par
                      <ColumnFilter
                        type="autocomplete"
                        value={filters.user_id}
                        onChange={(v) => update('user_id', v)}
                        label="créé par"
                        color={COMMERCIAL_COLOR}
                        options={users.map(u => ({ value: u.id.toString(), label: u.full_name || u.email }))}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : facturesList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      Aucune facture trouvée
                    </TableCell>
                  </TableRow>
                ) : (
                  facturesList.map((facture) => {
                    const isAcompte = facture.type === 'acompte';
                    const blRefs = getBLReferences(facture);
                    const designations = getDesignations(facture);

                    return (
                      <TableRow key={facture.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(facture.date).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 150 }}>
                          <Tooltip title={facture.client_name || ''} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {facture.client_name}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {facture.reference}
                        </TableCell>
                        <TableCell>
                          {isAcompte ? (
                            <Chip label="Acompte" size="small" color="info" variant="outlined" />
                          ) : (
                            <Chip label="Normale" size="small" color="default" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={getBCNumerosForTooltip(facture)} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {facture.bc_numero || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {isAcompte ? (
                            <Tooltip title={`Devis: ${facture.devis_reference || '-'} (${Number(facture.acompte_percentage || 0)}%)`} arrow>
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {facture.devis_reference || '-'}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Tooltip title={getBLReferencesForTooltip(facture)} arrow>
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {blRefs}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Tooltip title={getDesignationsForTooltip(facture)} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {designations}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {Number(
                            facture.acompte_deductions && facture.acompte_deductions.length > 0
                              ? facture.net_a_payer
                              : facture.total_net
                          ).toLocaleString('fr-FR', {
                            minimumFractionDigits: 2,
                          })}{' '}
                          DH
                        </TableCell>
                        <TableCell align="center">
                          {getStatusChip(facture.status)}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {facture.user_name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => previewFacturePdf(facture)}
                            title="Imprimer"
                          >
                            <PrintIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setPreviewFacture(facture)}
                            title="Aperçu"
                          >
                            <ViewIcon />
                          </IconButton>
                          <PermissionButton
                            moduleName="Commercial"
                            operation="edit"
                            miniModuleName="Factures"
                            component="IconButton"
                            onClick={() => handleOpenEditFacture(facture.id, facture.type || 'normale')}
                          >
                            <EditIcon />
                          </PermissionButton>
                          <PermissionButton
                            moduleName="Commercial"
                            operation="delete"
                            miniModuleName="Factures"
                            component="IconButton"
                            onClick={() => {
                              showConfirm(
                                `Êtes-vous sûr de vouloir supprimer la facture "${facture.reference}" ?`,
                                () => handleDelete(facture),
                                {
                                  title: 'Confirmer la suppression',
                                  confirmText: 'Supprimer',
                                  cancelText: 'Annuler',
                                }
                              );
                            }}
                            color="error"
                          >
                            <DeleteIcon />
                          </PermissionButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {tabValue === 1 && (
        <>
          <TableContainer component={Paper} sx={getModuleTableTheme('commercial')}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Facture N°</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell align="right">Montant</TableCell>
                  <TableCell align="center">Statut</TableCell>
                  <TableCell>Date de Suppression</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : deletedFacturesList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Aucune facture supprimée
                    </TableCell>
                  </TableRow>
                ) : (
                  deletedFacturesList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((facture) => (
                      <TableRow key={facture.id}>
                        <TableCell>
                          {new Date(facture.date).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>{facture.reference}</TableCell>
                        <TableCell>{facture.client_name}</TableCell>
                        <TableCell align="right">
                          {Number(
                            facture.acompte_deductions && facture.acompte_deductions.length > 0
                              ? facture.net_a_payer
                              : facture.total_net
                          ).toLocaleString('fr-FR', {
                            minimumFractionDigits: 2,
                          })}{' '}
                          DH
                        </TableCell>
                        <TableCell align="center">{getStatusChip(facture.status)}</TableCell>
                        <TableCell>
                          {facture.deleted_at
                            ? new Date(facture.deleted_at).toLocaleDateString('fr-FR')
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleRestore(facture)}
                            color="primary"
                            title="Restaurer"
                          >
                            <RestoreIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={deletedFacturesList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* Preview Modal */}
      {previewFacture && (
        <FacturePreview
          facture={previewFacture}
          open={!!previewFacture}
          onClose={() => setPreviewFacture(null)}
          onPrint={() => {
            const f = previewFacture;
            setPreviewFacture(null);
            previewFacturePdf(f);
          }}
          onEdit={() => {
            const type = previewFacture.type || 'normale';
            setPreviewFacture(null);
            handleOpenEditFacture(previewFacture.id, type);
          }}
        />
      )}

      {/* Form Dialog */}
      <FactureFormDialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        onSuccess={handleFormSuccess}
        factureId={editFactureId}
        factureType={factureType}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default FactureList;
